import type { FC } from 'react';
import { Box } from '@mui/material';
import type { FilterOptions } from '../../hooks/useCreativeFilters';
import type { LaunchStatus, UploadedFilter } from '../../types/hypothesis';
import SearchInput from './SearchInput';
import UploadedFilterSelect from './UploadedFilterSelect';
import StatusFilterSelect from './StatusFilterSelect';
import HypothesisAutocomplete from './HypothesisAutocomplete';
import MultiAutocompleteFilter from './MultiAutocompleteFilter';
import SingleAutocompleteFilter from './SingleAutocompleteFilter';
import TpcFinishDateInput from './TpcFinishDateInput';
import type { FilterSize } from './filterSize';
import { DEFAULT_FILTER_SIZE } from './filterSize';

interface CreativesFilterBarProps {
  search: string;
  uploaded: UploadedFilter;
  hypothesis: string[];
  flow: string[];
  customer: string | null;
  technology: string[];
  tpcFinishDate: string | null;
  status: LaunchStatus | null;
  filterOptions: FilterOptions;
  onSearchChange: (val: string) => void;
  onUploadedChange: (val: UploadedFilter) => void;
  onHypothesisChange: (val: string[]) => void;
  onFlowChange: (val: string[]) => void;
  onCustomerChange: (val: string | null) => void;
  onTechnologyChange: (val: string[]) => void;
  onTpcFinishDateChange: (val: string | null) => void;
  onStatusChange: (val: LaunchStatus | null) => void;
  size?: FilterSize;
}

const CreativesFilterBar: FC<CreativesFilterBarProps> = ({
  search,
  uploaded,
  hypothesis,
  flow,
  customer,
  technology,
  tpcFinishDate,
  status,
  filterOptions,
  onSearchChange,
  onUploadedChange,
  onHypothesisChange,
  onFlowChange,
  onCustomerChange,
  onTechnologyChange,
  onTpcFinishDateChange,
  onStatusChange,
  size = DEFAULT_FILTER_SIZE,
}) => {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, rowGap: 2, alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
      <SearchInput value={search} onChange={onSearchChange} size={size} />
      <UploadedFilterSelect value={uploaded} onChange={onUploadedChange} size={size} />
      <HypothesisAutocomplete
        value={hypothesis}
        options={filterOptions.hypotheses}
        onChange={onHypothesisChange}
        size={size}
      />
      <MultiAutocompleteFilter
        label="Flow"
        value={flow}
        options={filterOptions.flows}
        onChange={onFlowChange}
        size={size}
      />
      <SingleAutocompleteFilter
        label="Customer"
        value={customer}
        options={filterOptions.customers}
        onChange={onCustomerChange}
        clearable
        showCount
        size={size}
      />
      <MultiAutocompleteFilter
        label="Technology"
        value={technology}
        options={filterOptions.technologies}
        onChange={onTechnologyChange}
        size={size}
      />
      <TpcFinishDateInput
        value={tpcFinishDate}
        onChange={onTpcFinishDateChange}
        size={size}
      />
      <StatusFilterSelect value={status} onChange={onStatusChange} size={size} />
    </Box>
  );
};

export default CreativesFilterBar;
